import React, { useEffect, useRef, useState } from "react";
import "./PosCustomDateFilter.css";
import PosCustomIcon from "../PosCustomIcon/PosCustomIcon";
import { XMarkIcon } from "@heroicons/react/24/outline";

// ── helpers ──────────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, "0");

const formatDisplay = (date) => {
  if (!date) return "";
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

const formatReturnDate = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const parseInputDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  if (typeof dateVal === "string" && dateVal.includes("-")) {
    const parts = dateVal.split("-");
    if (parts.length === 3) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
  }
  return new Date(dateVal);
};

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Returns the 6-week grid of Date objects for a given month view
const buildCalendarGrid = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];

  // prev month tail
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, daysInPrevMonth - i), outside: true });
  }
  // current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(year, month, d), outside: false });
  }
  // next month head — fill to complete 6 rows (42 cells)
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  }

  return cells;
};

// ── component ─────────────────────────────────────────────────────────────────

/**
 * PosCustomDateFilter
 *
 * Renders a custom date picker filter pill with an inline calendar dropdown.
 * The component supports displaying a selected date, clearing the selection,
 * and notifying the parent when the date changes.
 *
 * @param {Object} props
 * @param {string|Date|null} props.selectedDate - The currently selected date value.
 *   Accepts a `Date` object, an ISO-like string (`YYYY-MM-DD`), or `null`.
 * @param {(value: string|null) => void} props.onChange - Callback invoked when
 *   the selected date changes or is cleared. The value is returned as a
 *   `YYYY-MM-DD` string or `null`.
 * @returns {JSX.Element}
 */
const PosCustomDateFilter = ({ selectedDate, onChange }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parsedSelectedDate = parseInputDate(selectedDate);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((parsedSelectedDate || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((parsedSelectedDate || today).getMonth());
  const [dropdownStyle, setDropdownStyle] = useState({ left: 0, right: "auto" });

  const wrapperRef = useRef(null);

  // Auto-adjust dropdown position if it overflows the right side of the screen
  useEffect(() => {
    if (open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      // Dropdown width is ~280px + padding. We use 320px as a safe threshold.
      if (rect.left + 320 > window.innerWidth) {
        setDropdownStyle({ right: 0, left: "auto" });
      } else {
        setDropdownStyle({ left: 0, right: "auto" });
      }
    }
  }, [open]);

  // Sync view when selectedDate changes externally
  useEffect(() => {
    if (parsedSelectedDate) {
      setViewYear(parsedSelectedDate.getFullYear());
      setViewMonth(parsedSelectedDate.getMonth());
    }
  }, [selectedDate]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const prevMonth = () => {
    let newMonth, newYear;
    if (viewMonth === 0) {
      newMonth = 11;
      newYear = viewYear - 1;
    } else {
      newMonth = viewMonth - 1;
      newYear = viewYear;
    }
    setViewMonth(newMonth);
    setViewYear(newYear);
  };

  const nextMonth = () => {
    let newMonth, newYear;
    if (viewMonth === 11) {
      newMonth = 0;
      newYear = viewYear + 1;
    } else {
      newMonth = viewMonth + 1;
      newYear = viewYear;
    }
    setViewMonth(newMonth);
    setViewYear(newYear);
  };

  const handleDayClick = (cell) => {
    if (cell.outside) return;
    const d = new Date(cell.date);
    d.setHours(0, 0, 0, 0);
    const formatted = formatReturnDate(d); // always "yyyy-mm-dd"
    if (onChange) {
      onChange(formatted);
    }
    setOpen(false);
  };

  const handleClear = () => {
    if (onChange) {
      onChange(null);
    }
    setOpen(false);
  };

  const handleToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const formatted = formatReturnDate(d); // "yyyy-mm-dd"
    if (onChange) {
      onChange(formatted);
    }
    setOpen(false);
  };

  const grid = buildCalendarGrid(viewYear, viewMonth);

  return (
    <div className="pos-custom-date-filter-wrapper" ref={wrapperRef}>
      {/* ── Trigger pill ── */}
      <div className="pos-custom-date-filter-pill" onClick={() => setOpen((p) => !p)}>
        {/* Left calendar icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#90A1B9"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-calendar-days pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        >
          <path d="M8 2v4"></path>
          <path d="M16 2v4"></path>
          <rect width="18" height="18" x="3" y="4" rx="2"></rect>
          <path d="M3 10h18"></path>
          <path d="M8 14h.01"></path>
          <path d="M12 14h.01"></path>
          <path d="M16 14h.01"></path>
          <path d="M8 18h.01"></path>
          <path d="M12 18h.01"></path>
          <path d="M16 18h.01"></path>
        </svg>

        <div
          className="pos-custom-date-filter-text textAlignCorrection"
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          {!parsedSelectedDate ? (
            "Date"
          ) : (
            <>
              {isSameDay(parsedSelectedDate, today) ? "Today" : formatDisplay(parsedSelectedDate)}
              <PosCustomIcon
                icon={<XMarkIcon />}
                height={"14px"}
                width={"14px"}
                pointer={true}
                onClickFunction={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              />
            </>
          )}
        </div>
      </div>

      {/* ── Dropdown calendar ── */}
      {open && (
        <div className="pos-custom-date-dropdown" style={dropdownStyle}>
          {/* Header */}
          <div className="pos-custom-date-header">
            <span className="pos-custom-date-month-label">
              {MONTHS[viewMonth]}, {viewYear}
            </span>
            <div className="pos-custom-date-nav">
              <button
                className="pos-custom-date-nav-btn"
                onClick={prevMonth}
                title="Previous month"
              >
                ↑
              </button>
              <button className="pos-custom-date-nav-btn" onClick={nextMonth} title="Next month">
                ↓
              </button>
            </div>
          </div>

          {/* Day-of-week labels */}
          <div className="pos-custom-date-day-labels">
            {DAYS.map((d) => (
              <span key={d} className="pos-custom-date-day-label">
                {d}
              </span>
            ))}
          </div>

          {/* Grid */}
          <div className="pos-custom-date-grid">
            {grid?.map((cell, i) => {
              const isSelected = isSameDay(cell?.date, parsedSelectedDate);
              const isToday = isSameDay(cell?.date, today);

              let cellClass = "pos-custom-date-cell";
              if (cell?.outside) cellClass += " pos-custom-date-cell-outside";
              if (isSelected) cellClass += " pos-custom-date-cell-selected";
              else if (isToday) cellClass += " pos-custom-date-cell-today";

              return (
                <div
                  key={i}
                  className={cellClass}
                  onClick={() => handleDayClick(cell)}
                  style={{ position: "relative" }}
                >
                  {cell?.date?.getDate()}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="pos-custom-date-footer">
            <button className="pos-custom-date-footer-btn" onClick={handleClear}>
              Clear
            </button>
            <button className="pos-custom-date-footer-btn" onClick={handleToday}>
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosCustomDateFilter;
